import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@lcc/ui";

interface Props {
  /** What failed, for the message, e.g. "Station board". */
  label: string;
  /** When this changes (e.g. the signed-in person changes) a failed screen tries again, without remounting. */
  resetKey?: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Contains a render error to one screen: the rest of the module (navigation) and the Hub
 * shell keep working, and the person can try the screen again.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`FER: the ${this.props.label} screen failed to render`, error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="fer-screen" role="alert">
        <p>
          <span aria-hidden="true">⚠ </span>
          {this.props.label} could not be shown because some of its data was not in the expected form. The rest of
          FER still works.
        </p>
        <Button variant="secondary" onClick={() => this.setState({ failed: false })}>
          Try again
        </Button>
      </div>
    );
  }
}
